<template>
	<base-modal
		ref="$modal"
		close-by-click-outside
		fullscreen-on-mobile
		:title="$t('messages_label')"
		width="640px"
		height="80vh"
		@on-close="onClose"
	>
		<div
			ref="$content"
			class="messages-modal"
		>
			<template v-if="['create-chat', 'edit-chat'].includes(mode)">
				<header-chat-create-dialog-panel />
			</template>
			<template v-else>
				<header-chat-search-panel
					v-model="searchStr"
					@request-focus="focusMessageInput"
				/>
				<div class="messages-modal__content">
					<chat-search-list
						v-if="isGlobalSearchStarted"
						:search="searchStr"
						@on-open-dialog="onOpenDialog"
						@on-open-message="onOpenDialog"
					/>
					<header-chat-messages-panel
						v-else-if="isMessagesSearchStarted"
						ref="messagesPanelRef"
						:mode="mode"
						@on-open-message="onOpenSearchedMessage"
						@on-navigate="onNavigateFromMessage"
					/>
					<component
						v-else
						:is="modeComponent[mode]"
						ref="messagesPanelRef"
						:loading="loading"
						:dialogs="dialogs"
						:mode="mode"
						:key="mode"
						@on-open="onOpenDialog"
						@on-delete="onDeleteDialog"
						@on-open-message="onOpenSearchedMessage"
						@on-navigate="onNavigateFromMessage"
					/>
				</div>
			</template>
		</div>
	</base-modal>
</template>

<script setup lang="ts">
import {
	computed,
	ref,
	watch
} from "vue"
import { debounce } from "lodash"
import BaseModal from "@/components/common/modal/BaseModal.vue"
import HeaderChatSearchPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatSearchPanel.vue"
import HeaderChatDialogsPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatDialogsPanel.vue"
import HeaderChatMessagesPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatMessagesPanel.vue"
import HeaderChatCreateDialogPanel from "@/components/layout/AppHeader/chat-panel/HeaderChatCreateDialogPanel.vue"
import ChatSearchList from "@/components/main/chat-panel/ChatSearchList.vue"
import { ChatMessage, Dialog } from "@/store/chats"
import { ChatsApi, SearchDialog, SearchMessage } from "@/ api/ChatsApi"
import { headerStore } from "@/store/header"
import { useModalEscape } from "@/composables/useModalEscape"
import { getCurrentLocalDateTime } from "@/utils/auxfunc"

const store = headerStore()
const $content = ref<HTMLElement | null>(null)
const $modal = ref<typeof BaseModal | null>(null)
const messagesPanelRef = ref<any>(null)

const { activate: activateEsc, deactivate: deactivateEsc } = useModalEscape({
	content: $content,
	close: () => $modal.value?.onClose()
})

const searchStr = ref('')
const isGlobalSearchStarted = ref(false)
const isMessagesSearchStarted = ref(false)
const suppressSearchWatch = ref(false)

const dialogs = computed(() => store.getDialogs)
const loading = computed(() => store.isDialogsLoading)
const openedChatId = computed(() => store.getOpenedChatId)
const mode = computed(() => store.getChatMode)

const modeComponent = computed(() => ({
	list: HeaderChatDialogsPanel,
	chat: HeaderChatMessagesPanel,
	'search-messages': HeaderChatMessagesPanel,
	'create-chat': HeaderChatCreateDialogPanel,
	'edit-chat': HeaderChatCreateDialogPanel,
	search: HeaderChatDialogsPanel,
} as Record<string, any>))

const focusMessageInput = () => {
	messagesPanelRef.value?.focusSync?.()
}

const onOpenDialog = async (dialog: Dialog | SearchDialog | SearchMessage, isCreated = false) => {
	focusMessageInput()
	suppressSearchWatch.value = true
	isGlobalSearchStarted.value = false
	isMessagesSearchStarted.value = false
	searchStr.value = ''
	if ('ChatId' in dialog) {
		const target = dialogs.value.find(d => d.Id === dialog.ChatId)
		if (target) {
			store.addOpenedChatsHistory(target)
		}
		store.setMode('chat')
		await store.setOpenedChat(dialog.ChatId, dialog.Id)
	} else {
		store.addOpenedChatsHistory({
			Id: dialog.Id,
			Name: dialog.Name,
			ScrollTop: null
		})
		store.setMode('chat')
		await store.setOpenedChat(dialog.Id)
		if (isCreated) {
			try {
				await store.updateDialogInList(getCurrentLocalDateTime())
			} catch (e) {
				console.error(e)
			}
		}
	}
}

const onOpenSearchedMessage = async (message: ChatMessage) => {
	suppressSearchWatch.value = true
	isGlobalSearchStarted.value = false
	isMessagesSearchStarted.value = false
	searchStr.value = ''
	store.setMode('chat')
	await store.setOpenedChat(openedChatId.value, message.Id)
}

const onDeleteDialog = async (dialogId: number) => {
	try {
		await ChatsApi.deleteDialog(dialogId)
		await store.fetchDialogs()
	} catch (e) {
		console.error(e)
	}
}

const onOpenGlobalSearch = () => {
	if (!isGlobalSearchStarted.value && searchStr.value) {
		isGlobalSearchStarted.value = true
	}
}

const onCloseGlobalSearch = () => {
	isGlobalSearchStarted.value = false
}

const onSearchMessagesInChat = async () => {
	if (searchStr.value) {
		if (mode.value !== 'search-messages') {
			store.setMode('search-messages')
		}

		if (!isMessagesSearchStarted.value) {
			isMessagesSearchStarted.value = true
		}

		if (openedChatId.value) {
			await store.fetchSearchedMessages(openedChatId.value, searchStr.value)
		}
	}
}

const onCloseMessagesSearch = () => {
	isMessagesSearchStarted.value = false
	store.setMode('chat')
	store.setOpenedChat(openedChatId.value, null, true)
}

const debouncedGlobalSearch = debounce(onOpenGlobalSearch, 500)
const debouncedMessagesSearch = debounce(onSearchMessagesInChat, 500)

watch(searchStr, (newVal) => {
	if (suppressSearchWatch.value) {
		suppressSearchWatch.value = false
		return
	}
	switch (store.mode) {
		case 'list':
		case 'search':
			if (newVal) {
				debouncedGlobalSearch()
			} else {
				onCloseGlobalSearch()
			}
			return
		case 'chat':
		case 'search-messages':
			if (newVal) {
				debouncedMessagesSearch()
			} else {
				onCloseMessagesSearch()
			}
			return
	}
})

const open = async () => {
	$modal.value?.onOpen()
	activateEsc()
	await store.fetchDialogs()
}

const onClose = () => {
	deactivateEsc()
	store.setResendMessages(null)
}

const onNavigateFromMessage = () => {
	$modal.value?.onClose()
}

defineExpose({ open })
</script>

<style scoped>
.messages-modal {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.messages-modal__content {
	flex: 1;
	min-height: 0;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}
</style>
